import type { ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "~/lib/utils";

/**
 * One badge for every status in the system.
 *
 * The point of centralising this is that a status means the same thing on every
 * screen. "FAILED" is the same red on a transaction, a bridge order and a
 * webhook event, and nobody has to remember which green they used last time.
 *
 * Tones map onto the semantic tokens in styles.css -- never raw colours:
 *
 *   success  a good terminal state, nothing to do
 *   warning  needs a human eventually, but nothing is broken
 *   danger   broken, or money did not move
 *   info     in progress, moving on its own
 *   neutral  inert: cancelled, expired, superseded
 */
const badge = cva(
  "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap",
  {
    variants: {
      tone: {
        success: "border-success/25 bg-success/12 text-success",
        warning: "border-warning/30 bg-warning/15 text-warning",
        danger: "border-destructive/25 bg-destructive/12 text-destructive",
        info: "border-info/25 bg-info/12 text-info",
        neutral: "border-border bg-muted text-muted-foreground",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export type Tone = NonNullable<VariantProps<typeof badge>["tone"]>;

/**
 * Every status value the API can return, mapped to a tone.
 *
 * Keys are matched case-insensitively because the Go side is inconsistent by
 * design of its own history: payment_status and bridge_orders.status are
 * SCREAMING_CASE Postgres enums, while callback_deliveries.status and
 * webhook_events.status are lowercase strings.
 *
 * SUCCESSFULL is spelled that way in the database enum
 * (models.PaymentStatusSuccessful). It is not a typo here; correcting it would
 * silently stop matching.
 */
const TONES: Record<string, Tone> = {
  // transactions.payment_status (7) -- EXPIRED shares the bridge entry below
  initiated: "info",
  successfull: "success",
  settled: "success",
  failed: "danger",
  cancelled: "neutral",
  refunded: "warning",

  // callback_deliveries.status (5)
  pending: "info",
  delivering: "info",
  delivered: "success",
  exhausted: "danger",

  // webhook_events.status (3)
  received: "info",
  processed: "success",

  // bridge_orders.status (8) -- CREATED, FAILED, CANCELLED, REFUNDED shared above
  created: "info",
  payment_link_sent: "info",
  paid: "success",
  expired: "neutral",
  points_applied: "success",

  // bridge_payments.status (6). Five are shared with the maps above; COMPLETED
  // is this vocabulary's own word for what transactions calls SUCCESSFULL and
  // bridge_orders calls PAID. Three names for one outcome is the reason the
  // bridge detail screen shows all three rather than picking one.
  completed: "success",

  // invoices.status (4), shown as settlement statements. PAID is shared above.
  draft: "neutral",
  issued: "info",
  void: "neutral",
};

export function toneFor(status: string): Tone {
  return TONES[status.toLowerCase()] ?? "neutral";
}

/**
 * A badge on the tone scale, for states that are not an API status value.
 *
 * Derived states -- "callback configured", "no credentials" -- read as the same
 * kind of thing as a payment status and must sit on the same five tones, or the
 * dashboard grows a second green that means something slightly different.
 */
export function Pill({
  tone,
  children,
  className,
  title,
}: {
  tone: Tone;
  children: ReactNode;
  className?: string;
  /** A hover explanation. A pill is short by construction, and some derived
   *  states -- "stale lock" -- need a sentence the badge has no room for. */
  title?: string;
}) {
  return (
    <span className={cn(badge({ tone }), className)} title={title}>
      {children}
    </span>
  );
}

/** Renders an API status value. Unknown values degrade to a neutral badge
 *  rather than throwing -- a new enum value on the server must not blank a
 *  page here. */
export function StatusBadge({ status, className }: { status: string; className?: string }) {
  return (
    <Pill tone={toneFor(status)} className={className}>
      {status.replace(/_/g, " ").toLowerCase()}
    </Pill>
  );
}
